import type { Language } from '@/lib/index';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TranslationValue = string | Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TranslationDict = Record<string, any>;

export const translations: Record<Language, TranslationDict> = {
  EN: {
    nav: {
      about: 'About',
      services: 'Services',
      portfolio: 'Portfolio',
      process: 'Process',
      reviews: 'Reviews',
      pricing: 'Pricing',
      phone: '672-755-8341',
      cta: 'Get Free Quote',
      language: 'Language',
    },
    hero: {
      badge: "Canada's Premium Renovation Experts",
      headline1: 'Transform Your Space.',
      headline2: 'Built for Canada.',
      subtitle: 'Premium home renovation services across Canada. From hardwood flooring to full room transformations, we bring your vision to life with expert craftsmanship and transparent pricing.',
      ctaPrimary: 'Get Free Quote',
      ctaSecondary: 'View Our Work',
      stats: {
        projects: 'Projects Done',
        projectsValue: '300+',
        experience: 'Years Experience',
        experienceValue: '10+',
        priceRange: 'Price Range',
        priceRangeValue: '$2.5K-$50K',
        rating: 'Average Rating',
        ratingValue: '⭐ 5.0',
      },
      scrollIndicator: 'Scroll to explore',
    },
    about: {
      title: 'About Vancore',
      subtitle: 'Building Excellence Since 2016',
      description: 'Founded in 2016 and headquartered in Burnaby, BC, Vancore Building Solutions Inc. has grown into one of Canada\'s most trusted names in premium home renovation. We serve homeowners across Vancouver, Toronto, Calgary, Montreal, Ottawa, Edmonton, and Winnipeg, delivering projects that range from $2,500 flooring installations to $50,000 full-room transformations.',
      mission: 'Our mission is simple: transform your space with uncompromising quality, transparent communication, and expert craftsmanship. Every project is a partnership, and every detail matters.',
      values: {
        quality: {
          title: 'Quality First',
          description: 'We use only premium materials and proven techniques. Every installation is backed by our commitment to excellence and industry-leading warranties.',
        },
        transparency: {
          title: 'Transparency',
          description: 'No hidden fees, no surprises. You\'ll know exactly what you\'re paying for, with detailed quotes and regular project updates.',
        },
        craftsmanship: {
          title: 'Craftsmanship',
          description: 'Our team of licensed professionals brings decades of combined experience. We don\'t just renovate—we create spaces you\'ll love for years to come.',
        },
      },
      ctaPrimary: 'Get Free Quote',
      ctaSecondary: 'View Portfolio',
    },
    services: {
      title: 'Our Services',
      subtitle: 'Premium renovation solutions tailored to your needs',
      learnMore: 'Learn More',
      service1: {
        name: 'Hardwood & Luxury Vinyl Flooring',
        description: 'Transform your floors with premium hardwood or luxury vinyl plank installation. Expert craftsmanship, lifetime warranty.',
        startingPrice: 'Starting at $2,500',
      },
      service2: {
        name: 'Custom Cabinet Installation',
        description: 'Elevate your kitchen or bathroom with custom-built cabinetry. Precision fit, premium finishes, and timeless design.',
        startingPrice: 'Starting at $3,500',
      },
      service3: {
        name: 'Interior Design & Upgrade',
        description: 'Refresh your living spaces with modern design upgrades. From paint to fixtures, we handle every detail.',
        startingPrice: 'Starting at $5,000',
      },
      service4: {
        name: 'Full Room Renovation',
        description: 'Complete room transformations from concept to completion. Kitchen, bathroom, or living space—we do it all.',
        startingPrice: 'Starting at $15,000',
      },
    },
    statsBar: {
      years: '10+ Years',
      yearsLabel: 'In Business',
      projects: '300+ Projects',
      projectsLabel: 'Completed',
      rating: '5.0 Stars',
      ratingLabel: 'Average Rating',
      licensed: '100% Licensed',
      licensedLabel: 'Fully Insured',
    },
    portfolio: {
      title: 'Our Portfolio',
      subtitle: 'Explore our recent projects across Canada',
      filterAll: 'All',
      filterFlooring: 'Flooring',
      filterCabinets: 'Cabinets',
      filterInterior: 'Interior',
      viewDetails: 'View Details',
      budget: 'Budget',
      duration: 'Duration',
    },
    process: {
      title: 'Our Process',
      subtitle: 'From consultation to completion, we make renovation easy',
      step1: {
        title: 'Free Consultation',
        description: 'We visit your home, discuss your vision, and provide a detailed quote—no obligation, no pressure.',
      },
      step2: {
        title: 'Custom Design Plan',
        description: 'Our team creates a tailored design plan with material selections, timelines, and transparent pricing.',
      },
      step3: {
        title: 'Expert Installation',
        description: 'Licensed professionals execute the work with precision, keeping you updated every step of the way.',
      },
      step4: {
        title: 'Final Walkthrough',
        description: 'We conduct a thorough inspection with you, ensuring every detail meets our high standards before project completion.',
      },
    },
    reviews: {
      title: 'What Our Clients Say',
      subtitle: 'Real reviews from homeowners across Canada',
    },
    pricing: {
      title: 'Transparent Pricing',
      subtitle: 'Choose the package that fits your budget and vision',
      getStarted: 'Get Started',
      tier1: {
        name: 'Essential',
        priceRange: '$2,500 - $10,000',
        description: 'Perfect for single-room upgrades',
        feature1: 'Hardwood or luxury vinyl flooring',
        feature2: 'Basic cabinet installation',
        feature3: 'Paint and fixture upgrades',
        feature4: 'Standard materials',
        feature5: '1-year warranty',
        feature6: 'Project timeline: 1-2 weeks',
      },
      tier2: {
        name: 'Premium',
        priceRange: '$10,000 - $25,000',
        description: 'Most popular for kitchen & bathroom renovations',
        feature1: 'Custom cabinetry with premium finishes',
        feature2: 'High-end flooring options',
        feature3: 'Designer fixtures and hardware',
        feature4: 'Electrical and plumbing upgrades',
        feature5: '5-year warranty',
        feature6: 'Project timeline: 3-4 weeks',
        feature7: 'Dedicated project manager',
      },
      tier3: {
        name: 'Signature',
        priceRange: '$25,000 - $50,000',
        description: 'Full-scale transformations with luxury finishes',
        feature1: 'Complete room renovation',
        feature2: 'Luxury materials and custom millwork',
        feature3: 'Structural modifications',
        feature4: 'Smart home integration',
        feature5: 'Lifetime warranty on workmanship',
        feature6: 'Project timeline: 6-8 weeks',
        feature7: 'White-glove service',
        feature8: 'Post-completion support',
      },
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about working with Vancore',
      q1: 'How long does a typical renovation take?',
      a1: 'Project timelines vary based on scope. Flooring installations typically take 1-2 weeks, kitchen renovations 3-4 weeks, and full-room transformations 6-8 weeks. We provide a detailed timeline during your free consultation.',
      q2: 'Do you serve all of Canada?',
      a2: 'Yes! We serve major cities across Canada including Vancouver, Toronto, Calgary, Montreal, Ottawa, Edmonton, and Winnipeg. Contact us to confirm service availability in your area.',
      q3: 'Are you licensed and insured?',
      a3: 'Absolutely. Vancore Building Solutions Inc. is fully licensed, bonded, and insured. All our contractors hold valid trade certifications and we carry comprehensive liability insurance for your peace of mind.',
      q4: 'What payment methods do you accept?',
      a4: 'We accept bank transfers, certified checks, and major credit cards. Payment is typically structured as: 30% deposit, 40% at project midpoint, and 30% upon completion. We never require full payment upfront.',
      q5: 'Can I see examples of your work?',
      a5: 'Of course! Browse our portfolio section above to see recent projects across Canada. We can also provide references from past clients in your area during your consultation.',
      q6: 'What if I\'m not satisfied with the work?',
      a6: 'Your satisfaction is our priority. We conduct a final walkthrough before project completion and address any concerns immediately. All work is backed by our warranty—1 year for Essential, 5 years for Premium, and lifetime for Signature packages.',
      q7: 'Do you provide free consultations?',
      a7: 'Yes! We offer complimentary in-home consultations where we assess your space, discuss your vision, and provide a detailed quote. There\'s no obligation and no pressure—just honest advice and transparent pricing.',
      q8: 'What materials do you use?',
      a8: 'We source premium materials from trusted suppliers. For flooring, we work with brands like Shaw, Mohawk, and Armstrong. Cabinetry is custom-built using solid wood and high-quality hardware. We\'ll walk you through all material options during your consultation.',
    },
    blog: {
      title: 'Renovation Insights',
      subtitle: 'Expert tips and trends from our team',
      readMore: 'Read More',
      category: 'Category',
      author: 'Author',
      readTime: 'min read',
    },
    contact: {
      title: 'Get Your Free Quote',
      subtitle: 'Ready to transform your space? Contact us today.',
      info: {
        title: 'Contact Information',
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
        serviceArea: 'Service Area',
        serviceAreaValue: 'All of Canada',
      },
      form: {
        name: 'Full Name',
        namePlaceholder: 'John Smith',
        phone: 'Phone Number',
        phonePlaceholder: '(123) 456-7890',
        email: 'Email Address',
        emailPlaceholder: 'john@example.com',
        service: 'Service Interested In',
        servicePlaceholder: 'Select a service',
        serviceFlooring: 'Hardwood & Luxury Vinyl Flooring',
        serviceCabinets: 'Custom Cabinet Installation',
        serviceInterior: 'Interior Design & Upgrade',
        serviceRenovation: 'Full Room Renovation',
        description: 'Project Description',
        descriptionPlaceholder: 'Tell us about your project...',
        submit: 'Submit Request',
      },
    },
    footer: {
      company: {
        title: 'Company',
        description: 'Premium home renovation services across Canada since 2016. Licensed, insured, and committed to excellence.',
      },
      quickLinks: {
        title: 'Quick Links',
        about: 'About',
        services: 'Services',
        portfolio: 'Portfolio',
        process: 'Process',
        reviews: 'Reviews',
        pricing: 'Pricing',
      },
      services: {
        title: 'Our Services',
        flooring: 'Flooring',
        cabinets: 'Cabinets',
        interior: 'Interior Design',
        renovation: 'Full Renovation',
      },
      contact: {
        title: 'Contact Info',
        phone: '672-755-8341',
        email: 'info@vancorebuild.ca',
        address: '6378 Silver Ave, Burnaby BC V5H 4K3, Canada',
      },
      copyright: '© 2026 Vancore Building Solutions Inc. All rights reserved.',
    },
    modal: {
      service: {
        features: 'What\'s Included',
        howWeWork: 'How We Work',
        warranty: 'Warranty',
        cta: 'Get Free Quote',
      },
      project: {
        location: 'Location',
        budget: 'Budget',
        duration: 'Duration',
        style: 'Style',
        scopeOfWork: 'Scope of Work',
        challenge: 'The Challenge',
        solution: 'Our Solution',
      },
      blog: {
        by: 'By',
        readTime: 'min read',
      },
    },
  },
  ZH: {
    nav: {
      about: '关于我们',
      services: '服务项目',
      portfolio: '作品集',
      process: '服务流程',
      reviews: '客户评价',
      pricing: '价格方案',
      phone: '672-755-8341',
      cta: '免费报价',
      language: '语言',
    },
    hero: {
      badge: '加拿大顶级装修专家',
      headline1: '改造您的空间。',
      headline2: '为加拿大而建。',
      subtitle: '遍布加拿大的高端家居装修服务。从硬木地板到全屋改造，我们以专业工艺和透明定价实现您的愿景。',
      ctaPrimary: '免费报价',
      ctaSecondary: '查看作品',
      stats: {
        projects: '完成项目',
        projectsValue: '300+',
        experience: '从业年限',
        experienceValue: '10+',
        priceRange: '价格区间',
        priceRangeValue: '$2.5K-$50K',
        rating: '平均评分',
        ratingValue: '⭐ 5.0',
      },
      scrollIndicator: '向下滚动探索',
    },
    about: {
      title: '关于Vancore',
      subtitle: '自2016年以来追求卓越',
      description: 'Vancore Building Solutions Inc.成立于2016年，总部位于BC省本拿比，已成长为加拿大最值得信赖的高端家居装修品牌之一。我们为温哥华、多伦多、卡尔加里、蒙特利尔、渥太华、埃德蒙顿和温尼伯的业主提供服务，项目范围从2,500加元的地板安装到50,000加元的全屋改造。',
      mission: '我们的使命很简单：以毫不妥协的质量、透明的沟通和专业的工艺改造您的空间。每个项目都是一次合作，每个细节都很重要。',
      values: {
        quality: {
          title: '质量第一',
          description: '我们只使用优质材料和经过验证的技术。每项安装都有我们对卓越的承诺和行业领先的保修支持。',
        },
        transparency: {
          title: '透明度',
          description: '没有隐藏费用，没有意外。您将确切知道自己支付的费用，并获得详细的报价和定期的项目更新。',
        },
        craftsmanship: {
          title: '工艺',
          description: '我们的持证专业团队拥有数十年的综合经验。我们不仅仅是装修——我们创造您会喜爱多年的空间。',
        },
      },
      ctaPrimary: '免费报价',
      ctaSecondary: '查看作品集',
    },
    services: {
      title: '我们的服务',
      subtitle: '量身定制的高端装修解决方案',
      learnMore: '了解更多',
      service1: {
        name: '硬木和豪华乙烯基地板',
        description: '使用优质硬木或豪华乙烯基板材安装改造您的地板。专业工艺，终身保修。',
        startingPrice: '起价 $2,500',
      },
      service2: {
        name: '定制橱柜安装',
        description: '使用定制橱柜提升您的厨房或浴室。精确贴合，优质饰面，永恒设计。',
        startingPrice: '起价 $3,500',
      },
      service3: {
        name: '室内设计与升级',
        description: '通过现代设计升级焕新您的生活空间。从油漆到固定装置，我们处理每个细节。',
        startingPrice: '起价 $5,000',
      },
      service4: {
        name: '全屋装修',
        description: '从概念到完成的完整房间改造。厨房、浴室或生活空间——我们全都能做。',
        startingPrice: '起价 $15,000',
      },
    },
    statsBar: {
      years: '10+ 年',
      yearsLabel: '从业经验',
      projects: '300+ 项目',
      projectsLabel: '已完成',
      rating: '5.0 星',
      ratingLabel: '平均评分',
      licensed: '100% 持证',
      licensedLabel: '全面保险',
    },
    portfolio: {
      title: '我们的作品集',
      subtitle: '探索我们在加拿大各地的最新项目',
      filterAll: '全部',
      filterFlooring: '地板',
      filterCabinets: '橱柜',
      filterInterior: '室内',
      viewDetails: '查看详情',
      budget: '预算',
      duration: '工期',
    },
    process: {
      title: '我们的流程',
      subtitle: '从咨询到完成，我们让装修变得简单',
      step1: {
        title: '免费咨询',
        description: '我们访问您的家，讨论您的愿景，并提供详细报价——无义务，无压力。',
      },
      step2: {
        title: '定制设计方案',
        description: '我们的团队创建量身定制的设计方案，包括材料选择、时间表和透明定价。',
      },
      step3: {
        title: '专业安装',
        description: '持证专业人员精确执行工作，在每一步都让您了解最新情况。',
      },
      step4: {
        title: '最终验收',
        description: '我们与您一起进行彻底检查，确保每个细节在项目完成前都符合我们的高标准。',
      },
    },
    reviews: {
      title: '客户评价',
      subtitle: '来自加拿大各地业主的真实评价',
    },
    pricing: {
      title: '透明定价',
      subtitle: '选择适合您预算和愿景的方案',
      getStarted: '开始',
      tier1: {
        name: '基础版',
        priceRange: '$2,500 - $10,000',
        description: '适合单间升级',
        feature1: '硬木或豪华乙烯基地板',
        feature2: '基础橱柜安装',
        feature3: '油漆和固定装置升级',
        feature4: '标准材料',
        feature5: '1年保修',
        feature6: '项目时间：1-2周',
      },
      tier2: {
        name: '高级版',
        priceRange: '$10,000 - $25,000',
        description: '最受欢迎的厨房和浴室装修',
        feature1: '带优质饰面的定制橱柜',
        feature2: '高端地板选项',
        feature3: '设计师固定装置和五金件',
        feature4: '电气和管道升级',
        feature5: '5年保修',
        feature6: '项目时间：3-4周',
        feature7: '专属项目经理',
      },
      tier3: {
        name: '尊享版',
        priceRange: '$25,000 - $50,000',
        description: '带豪华饰面的全面改造',
        feature1: '完整房间装修',
        feature2: '豪华材料和定制细木工',
        feature3: '结构改造',
        feature4: '智能家居集成',
        feature5: '工艺终身保修',
        feature6: '项目时间：6-8周',
        feature7: '白手套服务',
        feature8: '完成后支持',
      },
    },
    faq: {
      title: '常见问题',
      subtitle: '关于与Vancore合作您需要知道的一切',
      q1: '典型装修需要多长时间？',
      a1: '项目时间因范围而异。地板安装通常需要1-2周，厨房装修需要3-4周，全屋改造需要6-8周。我们会在免费咨询期间提供详细的时间表。',
      q2: '你们服务整个加拿大吗？',
      a2: '是的！我们服务加拿大各大城市，包括温哥华、多伦多、卡尔加里、蒙特利尔、渥太华、埃德蒙顿和温尼伯。请联系我们确认您所在地区的服务可用性。',
      q3: '你们有执照和保险吗？',
      a3: '当然。Vancore Building Solutions Inc.拥有完整的执照、担保和保险。我们所有的承包商都持有有效的行业认证，我们还拥有全面的责任保险，让您安心。',
      q4: '你们接受哪些付款方式？',
      a4: '我们接受银行转账、保付支票和主要信用卡。付款通常分为：30%定金，项目中期40%，完成时30%。我们从不要求预付全款。',
      q5: '我可以看到你们的作品示例吗？',
      a5: '当然！浏览上面的作品集部分，查看我们在加拿大各地的最新项目。我们还可以在咨询期间提供您所在地区过往客户的推荐。',
      q6: '如果我对工作不满意怎么办？',
      a6: '您的满意是我们的首要任务。我们在项目完成前进行最终验收，并立即解决任何问题。所有工作都有我们的保修支持——基础版1年，高级版5年，尊享版终身保修。',
      q7: '你们提供免费咨询吗？',
      a7: '是的！我们提供免费上门咨询，评估您的空间，讨论您的愿景，并提供详细报价。没有义务，没有压力——只有诚实的建议和透明的定价。',
      q8: '你们使用什么材料？',
      a8: '我们从值得信赖的供应商采购优质材料。对于地板，我们与Shaw、Mohawk和Armstrong等品牌合作。橱柜使用实木和高质量五金件定制。我们会在咨询期间向您介绍所有材料选项。',
    },
    blog: {
      title: '装修见解',
      subtitle: '来自我们团队的专家提示和趋势',
      readMore: '阅读更多',
      category: '分类',
      author: '作者',
      readTime: '分钟阅读',
    },
    contact: {
      title: '获取免费报价',
      subtitle: '准备好改造您的空间了吗？今天就联系我们。',
      info: {
        title: '联系信息',
        phone: '电话',
        email: '邮箱',
        address: '地址',
        serviceArea: '服务区域',
        serviceAreaValue: '全加拿大',
      },
      form: {
        name: '全名',
        namePlaceholder: '张三',
        phone: '电话号码',
        phonePlaceholder: '(123) 456-7890',
        email: '电子邮箱',
        emailPlaceholder: 'zhang@example.com',
        service: '感兴趣的服务',
        servicePlaceholder: '选择服务',
        serviceFlooring: '硬木和豪华乙烯基地板',
        serviceCabinets: '定制橱柜安装',
        serviceInterior: '室内设计与升级',
        serviceRenovation: '全屋装修',
        description: '项目描述',
        descriptionPlaceholder: '告诉我们您的项目...',
        submit: '提交请求',
      },
    },
    footer: {
      company: {
        title: '公司',
        description: '自2016年以来在加拿大提供高端家居装修服务。持证、保险，致力于卓越。',
      },
      quickLinks: {
        title: '快速链接',
        about: '关于我们',
        services: '服务项目',
        portfolio: '作品集',
        process: '服务流程',
        reviews: '客户评价',
        pricing: '价格方案',
      },
      services: {
        title: '我们的服务',
        flooring: '地板',
        cabinets: '橱柜',
        interior: '室内设计',
        renovation: '全屋装修',
      },
      contact: {
        title: '联系信息',
        phone: '672-755-8341',
        email: 'info@vancorebuild.ca',
        address: '6378 Silver Ave, Burnaby BC V5H 4K3, Canada',
      },
      copyright: '© 2026 Vancore Building Solutions Inc. 版权所有。',
    },
    modal: {
      service: {
        features: '包含内容',
        howWeWork: '我们如何工作',
        warranty: '保修',
        cta: '免费报价',
      },
      project: {
        location: '位置',
        budget: '预算',
        duration: '工期',
        style: '风格',
        scopeOfWork: '工作范围',
        challenge: '挑战',
        solution: '我们的解决方案',
      },
      blog: {
        by: '作者',
        readTime: '分钟阅读',
      },
    },
  },
  FR: {
    nav: {
      about: 'À propos',
      services: 'Services',
      portfolio: 'Portfolio',
      process: 'Processus',
      reviews: 'Avis',
      pricing: 'Tarification',
      phone: '672-755-8341',
      cta: 'Devis gratuit',
      language: 'Langue',
    },
    hero: {
      badge: 'Experts en rénovation premium du Canada',
      headline1: 'Transformez votre espace.',
      headline2: 'Construit pour le Canada.',
      subtitle: 'Services de rénovation résidentielle haut de gamme partout au Canada. Du plancher de bois franc aux transformations complètes, nous donnons vie à votre vision avec un savoir-faire expert et une tarification transparente.',
      ctaPrimary: 'Devis gratuit',
      ctaSecondary: 'Voir nos réalisations',
      stats: {
        projects: 'Projets réalisés',
        projectsValue: '300+',
        experience: 'Années d\'expérience',
        experienceValue: '10+',
        priceRange: 'Gamme de prix',
        priceRangeValue: '2,5K$-50K$',
        rating: 'Note moyenne',
        ratingValue: '⭐ 5.0',
      },
      scrollIndicator: 'Faites défiler pour explorer',
    },
    about: {
      title: 'À propos de Vancore',
      subtitle: 'Construire l\'excellence depuis 2016',
      description: 'Fondée en 2016 et basée à Burnaby, en Colombie-Britannique, Vancore Building Solutions Inc. est devenue l\'un des noms les plus fiables du Canada en matière de rénovation résidentielle haut de gamme. Nous servons les propriétaires à Vancouver, Toronto, Calgary, Montréal, Ottawa, Edmonton et Winnipeg, réalisant des projets allant de 2 500 $ pour l\'installation de planchers à 50 000 $ pour des transformations complètes.',
      mission: 'Notre mission est simple : transformer votre espace avec une qualité sans compromis, une communication transparente et un savoir-faire expert. Chaque projet est un partenariat et chaque détail compte.',
      values: {
        quality: {
          title: 'Qualité d\'abord',
          description: 'Nous utilisons uniquement des matériaux de qualité supérieure et des techniques éprouvées. Chaque installation est soutenue par notre engagement envers l\'excellence et des garanties de premier plan dans l\'industrie.',
        },
        transparency: {
          title: 'Transparence',
          description: 'Pas de frais cachés, pas de surprises. Vous saurez exactement ce que vous payez, avec des devis détaillés et des mises à jour régulières du projet.',
        },
        craftsmanship: {
          title: 'Savoir-faire',
          description: 'Notre équipe de professionnels agréés apporte des décennies d\'expérience combinée. Nous ne rénovons pas seulement—nous créons des espaces que vous aimerez pendant des années.',
        },
      },
      ctaPrimary: 'Devis gratuit',
      ctaSecondary: 'Voir le portfolio',
    },
    services: {
      title: 'Nos services',
      subtitle: 'Solutions de rénovation premium adaptées à vos besoins',
      learnMore: 'En savoir plus',
      service1: {
        name: 'Plancher de bois franc et vinyle de luxe',
        description: 'Transformez vos planchers avec l\'installation de bois franc ou de vinyle de luxe. Savoir-faire expert, garantie à vie.',
        startingPrice: 'À partir de 2 500 $',
      },
      service2: {
        name: 'Installation d\'armoires sur mesure',
        description: 'Rehaussez votre cuisine ou salle de bain avec des armoires sur mesure. Ajustement précis, finitions premium et design intemporel.',
        startingPrice: 'À partir de 3 500 $',
      },
      service3: {
        name: 'Design d\'intérieur et amélioration',
        description: 'Rafraîchissez vos espaces de vie avec des améliorations de design moderne. De la peinture aux luminaires, nous gérons chaque détail.',
        startingPrice: 'À partir de 5 000 $',
      },
      service4: {
        name: 'Rénovation complète de pièce',
        description: 'Transformations complètes de pièce du concept à l\'achèvement. Cuisine, salle de bain ou espace de vie—nous faisons tout.',
        startingPrice: 'À partir de 15 000 $',
      },
    },
    statsBar: {
      years: '10+ ans',
      yearsLabel: 'En affaires',
      projects: '300+ projets',
      projectsLabel: 'Complétés',
      rating: '5.0 étoiles',
      ratingLabel: 'Note moyenne',
      licensed: '100% agréé',
      licensedLabel: 'Entièrement assuré',
    },
    portfolio: {
      title: 'Notre portfolio',
      subtitle: 'Explorez nos projets récents à travers le Canada',
      filterAll: 'Tous',
      filterFlooring: 'Planchers',
      filterCabinets: 'Armoires',
      filterInterior: 'Intérieur',
      viewDetails: 'Voir les détails',
      budget: 'Budget',
      duration: 'Durée',
    },
    process: {
      title: 'Notre processus',
      subtitle: 'De la consultation à l\'achèvement, nous facilitons la rénovation',
      step1: {
        title: 'Consultation gratuite',
        description: 'Nous visitons votre maison, discutons de votre vision et fournissons un devis détaillé—sans obligation, sans pression.',
      },
      step2: {
        title: 'Plan de design personnalisé',
        description: 'Notre équipe crée un plan de design sur mesure avec sélection de matériaux, calendriers et tarification transparente.',
      },
      step3: {
        title: 'Installation experte',
        description: 'Des professionnels agréés exécutent le travail avec précision, vous tenant informé à chaque étape.',
      },
      step4: {
        title: 'Visite finale',
        description: 'Nous effectuons une inspection approfondie avec vous, garantissant que chaque détail répond à nos normes élevées avant l\'achèvement du projet.',
      },
    },
    reviews: {
      title: 'Ce que disent nos clients',
      subtitle: 'Avis réels de propriétaires à travers le Canada',
    },
    pricing: {
      title: 'Tarification transparente',
      subtitle: 'Choisissez le forfait qui correspond à votre budget et votre vision',
      getStarted: 'Commencer',
      tier1: {
        name: 'Essentiel',
        priceRange: '2 500 $ - 10 000 $',
        description: 'Parfait pour les améliorations d\'une seule pièce',
        feature1: 'Plancher de bois franc ou vinyle de luxe',
        feature2: 'Installation d\'armoires de base',
        feature3: 'Améliorations de peinture et luminaires',
        feature4: 'Matériaux standard',
        feature5: 'Garantie 1 an',
        feature6: 'Calendrier du projet : 1-2 semaines',
      },
      tier2: {
        name: 'Premium',
        priceRange: '10 000 $ - 25 000 $',
        description: 'Le plus populaire pour les rénovations de cuisine et salle de bain',
        feature1: 'Armoires sur mesure avec finitions premium',
        feature2: 'Options de plancher haut de gamme',
        feature3: 'Luminaires et quincaillerie de designer',
        feature4: 'Améliorations électriques et de plomberie',
        feature5: 'Garantie 5 ans',
        feature6: 'Calendrier du projet : 3-4 semaines',
        feature7: 'Gestionnaire de projet dédié',
      },
      tier3: {
        name: 'Signature',
        priceRange: '25 000 $ - 50 000 $',
        description: 'Transformations à grande échelle avec finitions de luxe',
        feature1: 'Rénovation complète de pièce',
        feature2: 'Matériaux de luxe et menuiserie sur mesure',
        feature3: 'Modifications structurelles',
        feature4: 'Intégration de maison intelligente',
        feature5: 'Garantie à vie sur le travail',
        feature6: 'Calendrier du projet : 6-8 semaines',
        feature7: 'Service gants blancs',
        feature8: 'Support post-achèvement',
      },
    },
    faq: {
      title: 'Questions fréquemment posées',
      subtitle: 'Tout ce que vous devez savoir sur le travail avec Vancore',
      q1: 'Combien de temps prend une rénovation typique ?',
      a1: 'Les délais de projet varient selon la portée. Les installations de plancher prennent généralement 1-2 semaines, les rénovations de cuisine 3-4 semaines et les transformations complètes 6-8 semaines. Nous fournissons un calendrier détaillé lors de votre consultation gratuite.',
      q2: 'Servez-vous tout le Canada ?',
      a2: 'Oui ! Nous servons les grandes villes à travers le Canada, y compris Vancouver, Toronto, Calgary, Montréal, Ottawa, Edmonton et Winnipeg. Contactez-nous pour confirmer la disponibilité du service dans votre région.',
      q3: 'Êtes-vous agréé et assuré ?',
      a3: 'Absolument. Vancore Building Solutions Inc. est entièrement agréée, cautionnée et assurée. Tous nos entrepreneurs détiennent des certifications commerciales valides et nous avons une assurance responsabilité complète pour votre tranquillité d\'esprit.',
      q4: 'Quels modes de paiement acceptez-vous ?',
      a4: 'Nous acceptons les virements bancaires, les chèques certifiés et les principales cartes de crédit. Le paiement est généralement structuré comme suit : 30% d\'acompte, 40% à mi-projet et 30% à l\'achèvement. Nous n\'exigeons jamais le paiement complet à l\'avance.',
      q5: 'Puis-je voir des exemples de votre travail ?',
      a5: 'Bien sûr ! Parcourez notre section portfolio ci-dessus pour voir des projets récents à travers le Canada. Nous pouvons également fournir des références de clients passés dans votre région lors de votre consultation.',
      q6: 'Que se passe-t-il si je ne suis pas satisfait du travail ?',
      a6: 'Votre satisfaction est notre priorité. Nous effectuons une visite finale avant l\'achèvement du projet et répondons immédiatement à toute préoccupation. Tous les travaux sont couverts par notre garantie—1 an pour Essentiel, 5 ans pour Premium et à vie pour les forfaits Signature.',
      q7: 'Offrez-vous des consultations gratuites ?',
      a7: 'Oui ! Nous offrons des consultations à domicile gratuites où nous évaluons votre espace, discutons de votre vision et fournissons un devis détaillé. Il n\'y a aucune obligation et aucune pression—juste des conseils honnêtes et une tarification transparente.',
      q8: 'Quels matériaux utilisez-vous ?',
      a8: 'Nous nous approvisionnons en matériaux premium auprès de fournisseurs de confiance. Pour les planchers, nous travaillons avec des marques comme Shaw, Mohawk et Armstrong. Les armoires sont construites sur mesure en utilisant du bois massif et de la quincaillerie de haute qualité. Nous vous présenterons toutes les options de matériaux lors de votre consultation.',
    },
    blog: {
      title: 'Perspectives de rénovation',
      subtitle: 'Conseils d\'experts et tendances de notre équipe',
      readMore: 'Lire la suite',
      category: 'Catégorie',
      author: 'Auteur',
      readTime: 'min de lecture',
    },
    contact: {
      title: 'Obtenez votre devis gratuit',
      subtitle: 'Prêt à transformer votre espace ? Contactez-nous aujourd\'hui.',
      info: {
        title: 'Coordonnées',
        phone: 'Téléphone',
        email: 'Courriel',
        address: 'Adresse',
        serviceArea: 'Zone de service',
        serviceAreaValue: 'Tout le Canada',
      },
      form: {
        name: 'Nom complet',
        namePlaceholder: 'Jean Dupont',
        phone: 'Numéro de téléphone',
        phonePlaceholder: '(123) 456-7890',
        email: 'Adresse courriel',
        emailPlaceholder: 'jean@example.com',
        service: 'Service d\'intérêt',
        servicePlaceholder: 'Sélectionnez un service',
        serviceFlooring: 'Plancher de bois franc et vinyle de luxe',
        serviceCabinets: 'Installation d\'armoires sur mesure',
        serviceInterior: 'Design d\'intérieur et amélioration',
        serviceRenovation: 'Rénovation complète de pièce',
        description: 'Description du projet',
        descriptionPlaceholder: 'Parlez-nous de votre projet...',
        submit: 'Soumettre la demande',
      },
    },
    footer: {
      company: {
        title: 'Entreprise',
        description: 'Services de rénovation résidentielle premium à travers le Canada depuis 2016. Agréé, assuré et engagé envers l\'excellence.',
      },
      quickLinks: {
        title: 'Liens rapides',
        about: 'À propos',
        services: 'Services',
        portfolio: 'Portfolio',
        process: 'Processus',
        reviews: 'Avis',
        pricing: 'Tarification',
      },
      services: {
        title: 'Nos services',
        flooring: 'Planchers',
        cabinets: 'Armoires',
        interior: 'Design d\'intérieur',
        renovation: 'Rénovation complète',
      },
      contact: {
        title: 'Coordonnées',
        phone: '672-755-8341',
        email: 'info@vancorebuild.ca',
        address: '6378 Silver Ave, Burnaby BC V5H 4K3, Canada',
      },
      copyright: '© 2026 Vancore Building Solutions Inc. Tous droits réservés.',
    },
    modal: {
      service: {
        features: 'Ce qui est inclus',
        howWeWork: 'Comment nous travaillons',
        warranty: 'Garantie',
        cta: 'Devis gratuit',
      },
      project: {
        location: 'Emplacement',
        budget: 'Budget',
        duration: 'Durée',
        style: 'Style',
        scopeOfWork: 'Portée des travaux',
        challenge: 'Le défi',
        solution: 'Notre solution',
      },
      blog: {
        by: 'Par',
        readTime: 'min de lecture',
      },
    },
  },
};